/**
 * AuthContext - Gestion de l'authentification Admin/Manager
 *
 * Ce contexte gère l'authentification des utilisateurs back-office (ADMIN, MANAGER)
 * IMPORTANT: Ceci est différent de VendorSession (sélection vendeur pour POS)
 *
 * Rôles:
 * - ADMIN: Accès total (Dashboard, Catalogue, POS, Paramètres, Équipe)
 * - MANAGER: Accès partiel (Catalogue, POS, Commandes)
 * - CASHIER: Accès POS uniquement (pas de login requis, utilise VendorQuickBar)
 */
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTenant } from './TenantContext';
import { logger } from '../utils/logger';

export type UserRole = 'ADMIN' | 'MANAGER' | 'CASHIER';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  first_name?: string;
  last_name?: string;
  role: UserRole;
  active: boolean;
  tenant?: {
    id: string;
    business_name: string;
    slug: string;
  };
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  hasRole: (roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { tenant } = useTenant();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Charger l'utilisateur depuis localStorage au démarrage
  useEffect(() => {
    const loadUser = async () => {
      const storedUser = localStorage.getItem('auth_user');
      const token = localStorage.getItem('auth_token');

      if (!storedUser || !token) {
        setLoading(false);
        return;
      }

      let parsedUser: AuthUser;
      try {
        parsedUser = JSON.parse(storedUser);
      } catch {
        // Données corrompues → nettoyer
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_token');
        setLoading(false);
        return;
      }

      // Restauration OPTIMISTE de la session depuis le cache — indispensable pour le
      // fonctionnement hors-ligne (marché sans wifi). La validation réseau ci-dessous
      // ne peut que CONFIRMER une révocation (401/403) ; elle ne doit JAMAIS déconnecter
      // sur une simple erreur réseau (sinon : impossible de se ré-authentifier hors-ligne).
      const storedTenantRaw = localStorage.getItem('tenant');
      setUser(parsedUser);
      if (storedTenantRaw) {
        try {
          const { api } = await import('../services/api');
          api.setTenantId(JSON.parse(storedTenantRaw).id);
          logger.log('[AuthContext] Session restaurée depuis le cache (optimiste)');
        } catch (e) {
          logger.warn('[AuthContext] Erreur parsing tenant:', e);
        }
      }
      setLoading(false);

      // Validation réseau en arrière-plan (non bloquante)
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
        const validateResponse = await fetch(`${apiUrl}/auth/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        // Token réellement rejeté par le serveur → déconnexion
        if (validateResponse.status === 401 || validateResponse.status === 403) {
          logger.warn('[AuthContext] Token rejeté par le serveur (401/403), déconnexion');
          localStorage.removeItem('auth_user');
          localStorage.removeItem('auth_token');
          setUser(null);
          return;
        }

        if (validateResponse.ok) {
          // Vérif cohérence tenant JWT vs localStorage (sécurité multi-tenant)
          try {
            const tokenParts = token.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              const jwtTenantId = payload.tenant_id || null;
              let storedTenantId: string | null = null;
              if (storedTenantRaw) {
                try { storedTenantId = JSON.parse(storedTenantRaw).id || null; } catch { storedTenantId = null; }
              }
              if (jwtTenantId && storedTenantId && jwtTenantId !== storedTenantId) {
                logger.warn(
                  `[AuthContext] Tenant mismatch JWT (${jwtTenantId}) vs localStorage (${storedTenantId}), déconnexion forcée`,
                );
                localStorage.removeItem('auth_user');
                localStorage.removeItem('auth_token');
                localStorage.removeItem('tenant');
                setUser(null);
                return;
              }
            }
          } catch (e) {
            logger.warn('[AuthContext] JWT payload decode failed:', e);
          }
        }
        // Autres statuts (5xx, etc.) : serveur en peine, pas une révocation → on garde la session.
      } catch (err) {
        // Erreur réseau (hors-ligne) → on CONSERVE la session en cache. Ne PAS déconnecter.
        logger.warn('[AuthContext] Validation token impossible (réseau) — session cache conservée (hors-ligne)');
      }
    };

    loadUser();
  }, []);

  /**
   * Login Admin avec email + Password
   * Utilise l'authentification contre folies_master (tenant_owners)
   */
  const login = async (email: string, password: string): Promise<boolean> => {
    if (!tenant) {
      setError('Tenant non chargé');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Appeler l'API d'authentification admin
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      const response = await fetch(`${apiUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || 'Email ou mot de passe incorrect');
        setLoading(false);
        return false;
      }

      const data = await response.json();

      // 2. Log pour debug - la vérification tenant est faite côté API
      logger.log('[AuthContext] Login response:', {
        userTenantId: data.user.tenant.id,
        currentTenantId: tenant?.id,
        match: data.user.tenant.id === tenant?.id,
      });

      // Note: On ne bloque plus si le tenant frontend diffère du tenant utilisateur
      // L'API a déjà validé que l'utilisateur appartient à un tenant actif
      // On met à jour le tenant frontend avec celui de l'utilisateur
      if (data.user.tenant.id !== tenant?.id) {
        logger.log('[AuthContext] Tenant mismatch - updating to user tenant');
      }

      // 3. Créer l'utilisateur authentifié
      // Mapper le rôle "owner" de folies_master vers "ADMIN" de l'application
      const role = data.user.role.toLowerCase() === 'owner' ? 'ADMIN' : data.user.role.toUpperCase();

      const authUser: AuthUser = {
        id: data.user.id,
        email: data.user.email,
        name: `${data.user.first_name} ${data.user.last_name}`,
        first_name: data.user.first_name,
        last_name: data.user.last_name,
        role: role as UserRole,
        active: true,
        tenant: data.user.tenant,
      };

      // 4. Sauvegarder le token JWT et l'utilisateur
      localStorage.setItem('auth_user', JSON.stringify(authUser));
      localStorage.setItem('auth_token', data.access_token);

      // 5. S'assurer que le tenant est sauvegardé et synchronisé avec l'API client
      if (tenant) {
        localStorage.setItem('tenant', JSON.stringify(tenant));
        const { api } = await import('../services/api');
        api.setTenantId(tenant.id);
        logger.log('[AuthContext] Tenant synchronisé:', tenant.id);
      }

      setUser(authUser);
      setLoading(false);

      logger.log(`✅ Login admin réussi: ${authUser.name} (${authUser.role})`);
      return true;
    } catch (err: any) {
      logger.error('Login failed:', err);
      setError(
        !navigator.onLine
          ? 'Hors-ligne — connexion impossible. Reconnectez-vous au réseau. (Une session déjà ouverte reste active hors-ligne.)'
          : (err.message || 'Erreur lors de la connexion'),
      );
      setLoading(false);
      return false;
    }
  };

  /**
   * Déconnexion - Nettoie TOUTE la session (auth + tenant)
   * Fix: Inclut le nettoyage du tenant pour éviter les problèmes de reconnexion
   */
  const logout = async () => {
    // 1. Nettoyer l'authentification
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_token');

    // 2. Nettoyer le tenant pour forcer rechargement
    localStorage.removeItem('tenant');

    // 3. Réinitialiser les états
    setUser(null);
    setError(null);

    // 4. Réinitialiser l'API client
    const { api } = await import('../services/api');
    api.setTenantId(null);

    logger.log('👋 Déconnexion complète (auth + tenant)');
  };

  /**
   * Vérifier si l'utilisateur a l'un des rôles requis
   */
  const hasRole = (roles: UserRole[]): boolean => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        loading,
        error,
        login,
        logout,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook pour accéder au contexte d'authentification
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
